import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  AppBar as MuiAppBar,
  Switch,
  FormControlLabel,
  Theme,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, KeyboardArrowDown as ArrowDown, KeyboardArrowUp as ArrowUp } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { ActionButton, Flex, Heading, Image, View } from '@adobe/react-spectrum';
import { adminRailList, leftRailListItem } from './contant';
import { UserRole } from '../globalContant';
import { useMainContainerProvider } from '../mainContainerProvider';
import { useAppProvider } from '../applicationProvider';
import { toTitleCase } from '../util';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: -drawerWidth,
  ...(open && {
    marginLeft: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'center',
}));

interface MenuItemType {
  path: string;
  icon: string;
  name: string;
  isMultiTab?: boolean;
  multiTitle?: {
    path: string;
    icon: string;
    name: string;
  }[];
}

interface LeftRailProps {
  children?: React.ReactNode;
  setColorScheme: React.Dispatch<React.SetStateAction<'light' | 'dark'>>;
  colorScheme: 'light' | 'dark';
}

const renderNestedItems = (
  item: MenuItemType,
  index: number,
  openCommDropper: number | null,
  handleCommDroper: (index: number) => void,
  navigate: (path: string) => void,
  location: { pathname: string },
  theme: Theme
) => (
  <List key={`nested-${index}`}>
    <Flex
      UNSAFE_style={{
        backgroundColor:
          openCommDropper === index || (item.path.includes(location.pathname.split('/').slice(0, 2).join('/')) && item.path !== '/')
            ? theme.palette.action.selected
            : 'inherit',
      }}
    >
      <ListItemButton sx={{ width: '90%' }} onClick={() => handleCommDroper(index)}>
        <Image width={25} height={25} marginEnd={'size-100'} src={item.icon} alt="File preview" objectFit="contain" />
        <ListItemText primary={item.name} />
        <View width="10%" marginEnd="size-100">
          <ListItemIcon sx={{ display: 'flex', justifyContent: 'left' }}>{openCommDropper === index ? <ArrowUp /> : <ArrowDown />}</ListItemIcon>
        </View>
      </ListItemButton>
    </Flex>
    {openCommDropper === index &&
      item.multiTitle?.map((ele, idx) => (
        <ListItem
          key={`nested-${index}-${idx}`}
          disablePadding
          onClick={() => navigate(ele.path)}
          sx={{
            backgroundColor: location.pathname.includes(ele.path) && item.path !== '/' ? theme.palette.action.selected : theme.palette.background.default,
          }}
        >
          <ListItemButton
            sx={{
              paddingLeft: '20%',
              backgroundColor: location.pathname.includes(ele.path) ? theme.palette.action.selected : 'inherit',
              color: location.pathname.includes(ele.path) ? theme.palette.getContrastText(theme.palette.action.selected) : 'inherit',
              '& .MuiListItemIcon-root': {
                color: location.pathname.includes(ele.path) ? theme.palette.getContrastText(theme.palette.action.selected) : 'inherit',
              },
            }}
          >
            <Image width={25} height={25} marginEnd={'size-100'} src={ele.icon} alt="File preview" objectFit="contain" />
            <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '90%' } }} primary={ele.name} />
          </ListItemButton>
        </ListItem>
      ))}
  </List>
);

const LeftRail: React.FC<LeftRailProps> = ({ children, setColorScheme, colorScheme }) => {
  const [open, setOpen] = useState(true);
  const { setLoggedIn } = useAppProvider();
  const [openCommDropper, setOpenCommDropper] = useState<number | null>(null);
  const [openUserManu, setOpenUserManu] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { userType, instituteLogo, instituteName, profileLogo } = useMainContainerProvider();
  const theme = useTheme();

  const handleCommDroper = (index: number) => setOpenCommDropper(openCommDropper === index ? null : index);

  const handleLogout = (path: string) => {
    setLoggedIn(false);
    Cookies.remove('loggedin');
    navigate(path);
    Object.keys(Cookies.get()).forEach((cookie) => Cookies.remove(cookie));
    sessionStorage.clear();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ backgroundColor: theme.palette.background.default }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setOpen(true)}
            edge="start"
            sx={{ marginRight: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon sx={{ color: theme.palette.mode === 'light' ? '#123456' : '#fff' }} />
          </IconButton>
          <Flex width="80%" alignItems="center" gap="size-100">
            <Image UNSAFE_style={{ borderRadius: '50%' }} width="6%" height="6%" src={instituteLogo || '/images/dummyImage.png'} alt="School Logo" />
            {instituteName && (
              <Heading level={3} UNSAFE_style={{ color: theme.palette.text.primary }}>
                {toTitleCase(instituteName)}
              </Heading>
            )}
          </Flex>
          <Flex justifyContent="end" width="100%">
            <ActionButton isQuiet onPress={() => setOpenUserManu((prev) => !prev)}>
              <Image width={25} height={25} marginEnd={'size-100'} src={'/images/profile.png'} alt="File preview" objectFit="contain" />
            </ActionButton>

            <Menu
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              open={openUserManu}
              onClose={() => setOpenUserManu(false)}
            >
              <MenuItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Image
                  width={50}
                  height={50}
                  src={userType === UserRole.ADMIN ? instituteLogo || '' : profileLogo ? profileLogo : '/images/dummyImage.png'}
                  alt="Profile_Image"
                />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setOpenUserManu(false);
                  navigate('/profile');
                }}
                sx={{ gap: '10px' }}
              >
                <Image width={25} height={25} src={'/images/profile.png'} alt="File preview" objectFit="contain" />
                <ListItemText primary="Profile" />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setOpenUserManu(false);
                  handleLogout('./');
                }}
                sx={{ gap: '10px' }}
              >
                <Image width={25} height={25} src={'/images/logout.png'} alt="File preview" objectFit="contain" />
                <ListItemText primary="Logout" />
              </MenuItem>
              <Divider />
              <MenuItem disableRipple>
                <FormControlLabel
                  control={<Switch checked={colorScheme === 'dark'} onChange={() => setColorScheme(colorScheme === 'dark' ? 'light' : 'dark')} />}
                  label="Enable Dark Mode"
                />
              </MenuItem>
            </Menu>
          </Flex>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
          },
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <DrawerHeader>
          <IconButton onClick={() => setOpen(false)}>
            <MenuIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {[...leftRailListItem, ...(userType === UserRole.ADMIN ? adminRailList : [])].map((item, index) =>
            item.isMultiTab ? (
              renderNestedItems(item, index, openCommDropper, handleCommDroper, navigate, location, theme)
            ) : (
              <ListItem
                key={`single-${index}`}
                disablePadding
                onClick={() => {
                  setOpenCommDropper(null);
                  if (item.name !== 'Logout') navigate(item.path);
                }}
              >
                <ListItemButton
                  sx={{
                    backgroundColor: location.pathname.includes(item.path) && item.path !== '/' ? theme.palette.action.selected : 'inherit',
                  }}
                >
                  <Image width={25} height={25} marginEnd={'size-100'} src={item.icon} alt="File preview" objectFit="contain" />
                  <ListItemText primary={item.name} />
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
};

export default LeftRail;
